import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import { CallToAction } from '../../components/common/blocks';
import AccordionList, {
  IAccordionListItem,
} from '../../components/common/blocks/AccordionList';
import { ICallToActionProps } from '../../components/common/blocks/CallToAction';
import ReviewsCarousel, {
  IReviewsCarouselProps,
} from '../../components/common/blocks/ReviewsCarousel';
import TextblocksWithIcon, {
  ITextblocksWithIconProps,
} from '../../components/common/blocks/TextblocksWithIcon';
import TrustFacts, {
  ITrustFactsProps,
} from '../../components/common/blocks/TrustFacts';
import { ITrustedByProps } from '../../components/common/blocks/TrustedBy';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import { IContactProps } from '../../components/contact-us/Contact';
import { SellingPoints } from '../../components/country';
import { ISellingPoints } from '../../components/country/SellingPoints';
import { Hero } from '../../components/home';
import { IHeroComponentProps } from '../../components/home/Hero';
import ShortcutCountryList, {
  IShortcutCountryList,
} from '../../components/home/ShortcutCountryList';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  IBusinessKeyValuesComponent,
  ICallToActionComponent,
  IContactMethod,
  IContactUsForm,
  ICountryRegion,
  ICustomerReview,
  ICustomerReviewsComponent,
  ICustomerReviewsMeta,
  ILayoutPageProps,
  IMedia,
  ITextBlockWithIcon,
  ITrademarkSearchCountryLanding,
  ITrademarkServicesPage,
  ITrustedByComponent,
  ITrustFacts,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getCommonTranslations,
  getContactMethods,
  isUndefinedOrEmpty,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
  scrollToElement,
} from '../../shared/useCommon';
import {
  generateCustomerReviewsSchema,
  generateFaqsSchema,
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import {
  flatStrapiCollection,
  flatStrapiObject,
  formatDate,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getImageSrcWithFallback,
  getLocalizedSlugByCountryIso,
  getTranslation,
  keepObjectProperties,
} from '../../shared/useUtils';
import { IAppPage } from '../../store/slices/appSlice';
import { NextPageWithLayout } from '../_app';

export interface ITrademarkSearchServicesPageProps extends ILayoutPageProps {
  hero: IHeroComponentProps;
  shortcutCountryList: IShortcutCountryList;
  introductionTitle: string;
  introductionDescription: string;
  introductionImage: IMedia;
  faqsTitle: string;
  faqs: IAccordionListItem[];
  businessKeyValues: IBusinessKeyValuesComponent;
  customerReviews: ICustomerReviewsComponent;
  reviews: ICustomerReview[];
  trustedBy: ITrustedByComponent;
  contactUsForm: IContactUsForm;
  contactUsFormSuccessMessage: string;
  contactUsFormErrorMessage: string;
  contactMethods: IContactMethod[];
  callToAction: ICallToActionComponent;
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
  howItWorks: ITextblocksWithIconProps;
  customerReviewsMeta: ICustomerReviewsMeta;
  customerReviewsTopTipStart: string;
  customerReviewsTopTipEnd: string;
  customerReviewsBottomTip: string;
  trustFactsProps: ITrustFactsProps;
}

const TrademarkSearchServicesPage: NextPageWithLayout<
  ITrademarkSearchServicesPageProps
> = (props: ITrademarkSearchServicesPageProps) => {
  const router = useRouter();
  const [hydrated, setHydrated] = useState(false);

  const heroProps: IHeroComponentProps = {
    ...props.hero,
    secondaryButtonAction: () => scrollToElement('contact'),
  };

  const contactProps: IContactProps = {
    contactMethods: props.contactMethods.map((x) => ({
      type: x.type,
      label: getTranslation(x.labelTranslations, router.locale || 'en')
        .text as string,
      value:
        x.valueTranslations && x.valueTranslations.length > 0
          ? (getTranslation(x.valueTranslations, router.locale || 'en')
              .text as string)
          : undefined,
    })),
    titleTagName: 'h2',
    formLabels: {
      title: props.contactUsForm.title,
      subTitle: props.contactUsForm.subTitle,
      name: props.contactUsForm.nameLabel,
      email: props.contactUsForm.emailLabel,
      message: props.contactUsForm.messageLabel,
      submit: props.contactUsForm.submitLabel,
      submitSuccessMessage: props.contactUsFormSuccessMessage,
      submitErrorMessage: props.contactUsFormErrorMessage,
    },
  };

  const businessKeyValueProps: ISellingPoints = {
    title: props.businessKeyValues.title,
    description: props.businessKeyValues.description,
    sellingPointsItems: [
      {
        title: props.businessKeyValues.keyValueOneTitle,
        description: props.businessKeyValues.keyValueOneDescription,
        iconPath: '/assets/images/icons/ExpertAgentIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueTwoTitle,
        description: props.businessKeyValues.keyValueTwoDescription,
        iconPath: '/assets/images/icons/WorldWideServicesIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueThreeTitle,
        description: props.businessKeyValues.keyValueThreeDescription,
        iconPath: '/assets/images/icons/NoHiddenChargesIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueFourTitle,
        description: props.businessKeyValues.keyValueFourDescription,
        iconPath: '/assets/images/icons/FreeConsultIcon.svg',
      },
    ],
  };

  const reviewsCarouselProps: IReviewsCarouselProps = {
    averageScore: props.customerReviewsMeta.average,
    totalReviews: props.customerReviewsMeta.numberOfReviews,
    topTipStart: props.customerReviewsTopTipStart,
    topTipEnd: props.customerReviewsTopTipEnd,
    title: props.customerReviews.title,
    description: props.customerReviews.description,
    bottomTip: props.customerReviewsBottomTip,
    defaultLinkPath: props.customerReviews.buttonPath,
    reviews: props.reviews.map((review) => ({
      text: review.content,
      username: review.customerName,
      score: 5,
      date: formatDate(review.date),
      link: review.link,
    })),
  };

  const trustedByProps: ITrustedByProps = {
    title: props.trustedBy.title,
    companies: props.trustedBy.logos.map((x) => ({
      company: 'company',
      image: (
        <Image
          src={x.url as string}
          fill
          sizes="175px"
          alt={x.alternativeText || ''}
        />
      ),
    })),
  };

  const callToActionProps: ICallToActionProps = {
    title: props.callToAction.title,
    buttons: props.callToAction.buttons,
  };

  const TrustedByComponent = dynamic(
    () => import('../../components/common/blocks/TrustedBy')
  );

  const ContactComponent = dynamic(
    () => import('../../components/contact-us/Contact')
  );

  useEffect(() => {
    setHydrated(true);
  }, []);

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section">
        <div className="page-content-block">
          <Hero {...heroProps} />
        </div>
        <div className="page-content-block">
          <TrustFacts {...props.trustFactsProps} />
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <div className="two-columns">
            <div className="min-height-image-container">
              <Image
                src={getImageSrcWithFallback(props.introductionImage, 'large')}
                alt={
                  props.introductionImage.alternativeText ||
                  props.introductionTitle
                }
                fill
                sizes="(max-width: 768px) 100vw, 75vw"
                quality={90}
                style={{ objectFit: 'contain' }}
              />
            </div>
            <div className="paragraphs-lighter">
              <h2>{props.introductionTitle}</h2>
              <ReactMarkdown>{props.introductionDescription}</ReactMarkdown>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content-section">
        <div className="page-content-block">
          <ShortcutCountryList {...props.shortcutCountryList} />
        </div>
      </div>

      <hr className="separator" />

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <SellingPoints {...businessKeyValueProps} />
        </div>
      </div>

      <div className="page-content-section">
        <div className="page-content-block">
          <TextblocksWithIcon {...props.howItWorks} />
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <TrustedByComponent {...trustedByProps} />
        </div>
      </div>

      <div className="page-content-section">
        <div className="page-content-block">
          {hydrated && <ReviewsCarousel {...reviewsCarouselProps} />}
        </div>
      </div>

      {props.faqs && props.faqs.length > 0 && (
        <div className="page-content-section">
          <div className="page-content-block">
            <AccordionList title={props.faqsTitle || ''} items={props.faqs} />
          </div>
        </div>
      )}

      <div className="page-content-section is-narrower">
        <div className="page-content-block" id="contact">
          <ContactComponent {...contactProps} />
        </div>
      </div>

      <div className="page-content-section is-full-width">
        <div className="page-content-block" id="services">
          <CallToAction {...callToActionProps} />
        </div>
      </div>
    </div>
  );
};

TrademarkSearchServicesPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

export async function getStaticProps(
  context: any
): Promise<{ props: ITrademarkSearchServicesPageProps }> {
  const appRoutes = await getRoutes();
  const getLocalizedAppRoute = (actualPath: string) => {
    return (
      appRoutes?.find(
        (x: IAppPage) =>
          x &&
          x.destination.replace(`/${context.locale}`, '') === actualPath &&
          x.language === context.locale
      )?.source || actualPath
    );
  };

  const pageUrlParams = new URLSearchParams({
    'populate[0]': 'meta',
    'populate[1]': 'hero',
    'populate[2]': 'hero.background',
    'populate[3]': 'introductionTitle',
    'populate[4]': 'introductionDescription',
    'populate[5]': 'introductionImage',
    'populate[6]': 'quickAccessTitle',
    'populate[7]': 'quickAccessSubTitle',
    'populate[8]': 'quickAccessShowAllButton',
    'populate[9]': 'quickAccessShowAllButtonPath',
    'populate[10]': 'businessKeyValues',
    'populate[11]': 'howItWorksTitle',
    'populate[12]': 'howItWorks',
    'populate[13]': 'howItWorks.icon',
    'populate[14]': 'trustedBy',
    'populate[15]': 'trustedBy.logos',
    'populate[16]': 'customerReviews',
    'populate[17]': 'faqsTitle',
    'populate[18]': 'faqs',
    'populate[19]': 'contactUsForm',
    'populate[20]': 'contactUsFormSuccessMessage',
    'populate[21]': 'contactUsFormErrorMessage',
    'populate[22]': 'callToAction',
    'populate[23]': 'callToAction.buttons',
    'populate[24]': 'breadcrumbAncestors',
  });

  const customerReviewsUrlParams = {
    locale: context.locale,
  };

  const page = (
    await fetchStrapi<any>(
      `/trademark-search-services-page`,
      pageUrlParams,
      buildApiKey
    )
  ).data.attributes as ITrademarkServicesPage;
  const contactMethods = await getContactMethods();
  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = await populateFooterProps(context.locale);
  const commonTranslations = await getCommonTranslations();
  const reviews = flatStrapiCollection(
    await fetchStrapi<any>(
      `/customer-reviews`,
      customerReviewsUrlParams,
      buildApiKey
    )
  ) as ICustomerReview[];
  const trustedBy: ITrustedByComponent = getTranslation(
    page.trustedBy,
    context.locale
  );
  trustedBy.logos = flatStrapiCollection(trustedBy.logos as any).map((x) =>
    keepObjectProperties<IMedia>(x, ['url', 'alternativeText', 'name'])
  );
  const customerReviews: ICustomerReviewsComponent = getTranslation(
    page.customerReviews,
    context.locale
  );
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;

  // Check if customer reviews title contains placecholders. Example: "Average rating: {{average}} based on {{numberOfReviews}} reviews (Best rating: {{bestRating}})"
  if (
    customerReviews.title.includes('{{') &&
    customerReviews.title.includes('}}')
  ) {
    customerReviews.title = customerReviews.title.replace(
      '{{average}}',
      customerReviewsMeta.average.toString()
    );
    customerReviews.title = customerReviews.title.replace(
      '{{numberOfReviews}}',
      customerReviewsMeta.numberOfReviews.toString()
    );
    customerReviews.title = customerReviews.title.replace(
      '{{bestRating}}',
      customerReviewsMeta.bestRating.toString()
    );
  }

  // Prepare Hero
  const heroData = getTranslation(page.hero, context.locale);
  const heroProps: IHeroComponentProps = {
    title: heroData.title,
    description: heroData.description,
    buttonLabel: heroData.buttonLabel,
    buttonPath: heroData.buttonPath || '',
    secondaryButtonLabel: heroData.secondaryButtonLabel,
    backgroundUrl:
      getImageSrcWithFallback(flatStrapiObject(heroData.background), 'full') ||
      '/assets/images/home/map-image.png',
    titleHighlightedText: heroData.highlightedTitleText,
    backgroundAlt:
      flatStrapiObject<any>(heroData.background).alternativeText ||
      'Hero Background Image',
  };

  // Prepare countries list
  const trademarkLandingUrlParams = new URLSearchParams({
    'populate[0]': 'country',
    'populate[1]': 'meta',
    'pagination[start]': '0',
    'pagination[limit]': '-1',
  });
  const trademarkLandingsResult = await fetchStrapi<any>(
    `/trademark-search-landings`,
    trademarkLandingUrlParams,
    buildApiKey
  );
  const trademarkLandings: ITrademarkSearchCountryLanding[] =
    flatStrapiCollection<ITrademarkSearchCountryLanding>(
      trademarkLandingsResult
    );

  if (trademarkLandings.length > 0) {
    trademarkLandings.forEach((x) => {
      x.country = flatStrapiObject(x.country as any);
    });
  }

  const trademarkLandingsMinimalData = trademarkLandings.map((x) => ({
    country: { iso: x.country.iso },
    metas: x.meta.filter((y) => y.language === context.locale),
  }));

  const getShortcutCountryListFeaturedCountries = (region: ICountryRegion) => {
    const featuredCountries = region.featuredSearchCountries || [];
    return featuredCountries
      .map((c) => {
        const trademarkLandingSlug = getLocalizedSlugByCountryIso(
          trademarkLandingsMinimalData,
          c.iso,
          context.locale || 'en'
        );
        return {
          name: getTranslation(c.nameTranslations, context.locale || 'en')
            .text as string,
          link: getLocalizedAppRoute(
            `/trademark-search-services/${trademarkLandingSlug}`
          ),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const regionsUrlParams = {
    'populate[0]': 'nameTranslations',
    'populate[1]': 'featuredSearchCountries',
    'populate[2]': 'featuredSearchCountries.nameTranslations',
  };

  const regions = flatStrapiCollection(
    await fetchStrapi<any>(`/country-regions`, regionsUrlParams, buildApiKey)
  ) as ICountryRegion[];

  regions.forEach((x) => {
    if (
      x.featuredSearchCountries &&
      (x.featuredSearchCountries as any).data &&
      (x.featuredSearchCountries as any).data.length > 0
    ) {
      x.featuredSearchCountries = flatStrapiCollection(
        x.featuredSearchCountries as any
      );
    } else {
      x.featuredSearchCountries = [];
    }
  });

  const shortcutCountryList: IShortcutCountryList = {
    title: getTranslation(page.quickAccessTitle, context.locale).text as string,
    description: getTranslation(page.quickAccessSubTitle, context.locale)
      .text as string,
    showAllLabel: getTranslation(page.quickAccessShowAllButton, context.locale)
      .text as string,
    regions: regions
      .filter(
        (x) =>
          x.featuredSearchCountries &&
          (x.featuredSearchCountries as any).length > 0
      )
      .map((x) => ({
        name: getTranslation(x.nameTranslations, context.locale || 'en')
          .text as string,
        link: getTranslation(
          page.quickAccessShowAllButtonPath,
          context.locale || 'en'
        ).text as string,
        countries: getShortcutCountryListFeaturedCountries(x),
        order: x.order,
      }))
      .sort((a, b) => a.order - b.order),
  };

  // Populate breadcrumbs
  if (page.breadcrumbAncestors && page.breadcrumbAncestors.length > 0) {
    navBarProps.breadcrumbAncestors = page.breadcrumbAncestors?.filter(
      (x) => x.language === context.locale
    );
  }

  navBarProps.breadcrumbsHomePageDisplayName = getTranslation(
    commonTranslations.homePageName,
    context.locale
  ).text as string;
  navBarProps.breadcrumbsCurrentPageDisplayName = heroData.title as string;

  const faqsTitle =
    page.faqsTitle && page.faqsTitle.length > 0
      ? (getTranslation(page.faqsTitle, context.locale).text as string)
      : (getTranslation(commonTranslations.faqsTitle, context.locale)
          .text as string);
  const faqs = page?.faqs
    ?.filter((x) => x.language === context.locale)
    .map((x) => ({
      title: x.question,
      content: x.answer,
    })) as IAccordionListItem[];

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );
  const faqsSchema = generateFaqsSchema(
    faqs.map((x) => ({ question: x.title, answer: x.content }))
  );
  const customerReviewsSchema = generateCustomerReviewsSchema(
    reviews,
    customerReviewsMeta
  );

  const pageSchema: PageJSONLDSchema = {
    nodes: [
      ...websiteSchema,
      ...organizationSchema,
      ...faqsSchema,
      ...customerReviewsSchema,
    ],
  };

  const meta = getTranslation(page.meta, context.locale);
  const alternativeLinks = getAlternateLinks(
    '/trademark-search-services',
    appRoutes
  );
  const canonicalLink = getCanonicalLink(context.locale || 'en', meta.slug);
  const callToAction = getTranslation(page.callToAction, context.locale);

  if (callToAction.image && (callToAction.image as any).data) {
    callToAction.image = flatStrapiObject(callToAction.image) as IMedia;
  }

  const contactUsFormSuccessMessage = isUndefinedOrEmpty(
    page.contactUsFormSuccessMessage
  )
    ? (getTranslation(
        commonTranslations.contactUsFormSuccessMessage,
        context.locale
      ).text as string)
    : (getTranslation(page.contactUsFormSuccessMessage, context.locale)
        .text as string);
  const contactUsFormErrorMessage = isUndefinedOrEmpty(
    page.contactUsFormErrorMessage
  )
    ? (getTranslation(
        commonTranslations.contactUsFormErrorMessage,
        context.locale
      ).text as string)
    : (getTranslation(page.contactUsFormErrorMessage, context.locale)
        .text as string);

  const howItWorks: ITextblocksWithIconProps = {
    title: getTranslation(page.howItWorksTitle, context.locale).text as string,
    items: page.howItWorks
      .filter((x) => x.language === context.locale)
      .map((x: ITextBlockWithIcon) => ({
        content: x.content,
        icon: flatStrapiObject(x.icon as any) as IMedia,
      })),
  };

  const trustFacts: ITrustFacts = getTranslation(
    commonTranslations.trustFactsStrip,
    context.locale
  );

  const trustFactsProps: ITrustFactsProps = {
    locale: context.locale,
    facts: (trustFacts?.facts || []).map((x) => ({
      value: x.value,
      description: x.description,
    })),
  };

  const props: ITrademarkSearchServicesPageProps = {
    // Layout
    pageSchema,
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta,
    hero: heroProps,
    introductionTitle:
      getTranslation(page.introductionTitle, context.locale).text || '-',
    introductionDescription:
      getTranslation(page.introductionDescription, context.locale).text || '-',
    introductionImage: flatStrapiObject(page.introductionImage),
    businessKeyValues: getTranslation(page.businessKeyValues, context.locale),
    callToAction,
    trustedBy,
    customerReviews,
    reviews,
    contactMethods,
    contactUsForm: getTranslation(page.contactUsForm, context.locale),
    contactUsFormSuccessMessage,
    contactUsFormErrorMessage,
    canonicalLink,
    alternativeLinks,
    shortcutCountryList,
    howItWorks,
    faqsTitle,
    faqs,
    customerReviewsMeta,
    customerReviewsTopTipStart: getTranslation(
      commonTranslations.customerReviewsTopTipStart,
      context.locale
    ).text as string,
    customerReviewsTopTipEnd: getTranslation(
      commonTranslations.customerReviewsTopTipEnd,
      context.locale
    ).text as string,
    customerReviewsBottomTip: getTranslation(
      commonTranslations.customerReviewsBottomTip,
      context.locale
    ).text as string,
    trustFactsProps,
  };

  return {
    props,
  };
}

export default TrademarkSearchServicesPage;
